import type React from "react";
import {
  SiClickhouse,
  SiCloudflare,
  SiDatadog,
  SiFigma,
  SiGithub,
  SiGrafana,
  SiJira,
  SiLinear,
  SiNotion,
  SiPlanetscale,
  SiPosthog,
  SiSentry,
  SiSlack,
  SiSupabase,
} from "@icons-pack/react-simple-icons";
import { BlacksmithMcpIcon } from "./BlacksmithMcpIcon";

/**
 * Brand identity for known MCP providers: icon components, chip colors,
 * and URL host-suffix patterns for auto-detection.
 *
 * Provider catalog metadata (display name, description, default URL,
 * auth type, recommended status) now lives in the backend `mcp_providers`
 * table and is fetched via `useMcpProviders`. This file only handles the
 * visual/rendering side that requires React components.
 */

export type McpProviderIconComponent = React.ComponentType<{
  className?: string;
  size?: number | string;
}>;

export interface McpProviderBrandVariant {
  bg: string;
  fg: string;
}

export interface McpProviderBrand {
  Icon: McpProviderIconComponent;
  /** Tailwind classes for the chip background. */
  bg: string;
  /** Tailwind classes for the chip foreground (icon stroke). */
  fg: string;
  /** Override styles for the suggestion pill (rendered on gradient bg). */
  pill?: McpProviderBrandVariant;
  /** Override styles for small inline contexts (tool call labels). */
  inline?: McpProviderBrandVariant;
}

/** `toolcall` — monochrome glyph in tool-call rows (see McpProviderIcon). */
export type McpProviderBrandContext =
  | "default"
  | "pill"
  | "inline"
  | "toolcall";

export interface McpProviderDefinition {
  id: string;
  displayName: string;
  brand?: McpProviderBrand;
  /** Host suffixes (with leading dot) that auto-classify as this provider. */
  hostSuffixes?: ReadonlyArray<string>;
}

export const MCP_PROVIDERS: ReadonlyArray<McpProviderDefinition> = [
  {
    // Provider id a server named agent-browser reports (codesmith
    // tool_prefix.ts). Not a catalog provider (no backend mcp_providers
    // row); tool part metadata still carries it as mcpProviderId.
    id: "blacksmith-browser",
    displayName: "Blacksmith Browser",
    brand: {
      Icon: BlacksmithMcpIcon,
      bg: "bg-neutral-100 dark:bg-neutral-800",
      fg: "text-neutral-900 dark:text-neutral-100",
    },
  },
  {
    id: "linear",
    displayName: "Linear",
    brand: {
      Icon: SiLinear,
      bg: "bg-neutral-100 dark:bg-neutral-200",
      fg: "text-[#5e6ad2]",
    },
    hostSuffixes: [".linear.app"],
  },
  {
    id: "sentry",
    displayName: "Sentry",
    brand: {
      Icon: SiSentry,
      bg: "bg-neutral-100 dark:bg-neutral-200",
      fg: "text-[#362d59]",
    },
    hostSuffixes: [".sentry.dev", ".sentry.io"],
  },
  {
    id: "datadog",
    displayName: "Datadog",
    brand: {
      Icon: SiDatadog,
      bg: "bg-neutral-100 dark:bg-neutral-200",
      fg: "text-[#632ca6]",
    },
    hostSuffixes: [".datadoghq.com", ".datadoghq.eu", ".ddog-gov.com"],
  },
  {
    id: "github",
    displayName: "GitHub",
    brand: {
      Icon: SiGithub,
      bg: "bg-neutral-100 dark:bg-neutral-800",
      fg: "text-neutral-900 dark:text-neutral-100",
    },
    hostSuffixes: [".github.com"],
  },
  {
    id: "notion",
    displayName: "Notion",
    brand: {
      Icon: SiNotion,
      bg: "bg-neutral-100 dark:bg-neutral-800",
      fg: "text-neutral-900 dark:text-neutral-100",
    },
    hostSuffixes: [".notion.so", ".notion.com"],
  },
  {
    id: "slack",
    displayName: "Slack",
    brand: {
      Icon: SiSlack,
      bg: "bg-neutral-100 dark:bg-neutral-200",
      fg: "text-[#4a154b]",
    },
    hostSuffixes: [".slack.com"],
  },
  {
    id: "supabase",
    displayName: "Supabase",
    brand: {
      Icon: SiSupabase,
      bg: "bg-neutral-100 dark:bg-neutral-200",
      fg: "text-[#3fcf8e]",
    },
    hostSuffixes: [".supabase.com", ".supabase.co"],
  },
  {
    id: "posthog",
    displayName: "PostHog",
    brand: {
      Icon: SiPosthog,
      bg: "bg-neutral-100 dark:bg-neutral-200",
      fg: "text-[#1d4aff]",
    },
    hostSuffixes: [".posthog.com"],
  },
  {
    id: "clickhouse",
    displayName: "ClickHouse",
    brand: {
      Icon: SiClickhouse,
      // Default: dark icon on yellow chip (ClickHouse brand)
      bg: "bg-[#faff69]",
      fg: "text-neutral-900",
      // Pill/inline: dark icon, no colored chip
      pill: { bg: "bg-transparent", fg: "text-neutral-900" },
      inline: {
        bg: "bg-transparent",
        fg: "text-neutral-900 dark:text-neutral-100",
      },
    },
    hostSuffixes: [".clickhouse.cloud"],
  },
  {
    id: "cloudflare",
    displayName: "Cloudflare",
    brand: {
      Icon: SiCloudflare,
      bg: "bg-neutral-100 dark:bg-neutral-200",
      fg: "text-[#f38020]",
    },
    hostSuffixes: [".cloudflare.com"],
  },
  {
    id: "grafana",
    displayName: "Grafana",
    brand: {
      Icon: SiGrafana,
      bg: "bg-neutral-100 dark:bg-neutral-200",
      fg: "text-[#f46800]",
    },
    hostSuffixes: [".grafana.com", ".grafana.net"],
  },
  {
    id: "planetscale",
    displayName: "PlanetScale",
    brand: {
      Icon: SiPlanetscale,
      bg: "bg-neutral-100 dark:bg-neutral-800",
      fg: "text-neutral-900 dark:text-neutral-100",
    },
    hostSuffixes: [".planetscale.com", ".pscale.dev"],
  },
  {
    id: "jira",
    displayName: "Jira",
    brand: {
      Icon: SiJira,
      bg: "bg-neutral-100 dark:bg-neutral-200",
      fg: "text-[#0052cc]",
    },
    hostSuffixes: [".atlassian.com", ".atlassian.net"],
  },
  {
    id: "figma",
    displayName: "Figma",
    brand: {
      Icon: SiFigma,
      bg: "bg-neutral-100 dark:bg-neutral-200",
      fg: "text-neutral-500",
    },
    hostSuffixes: [".figma.com"],
  },
];

// ---------------------------------------------------------------------------
// Derived lookups — computed once at module load. Consumers (McpProviderIcon,
// mcpToolUtils) import these instead of touching MCP_PROVIDERS directly so
// the surface stays stable as the registry grows.
// ---------------------------------------------------------------------------

export const PROVIDER_BRANDS: Readonly<Record<string, McpProviderBrand>> =
  Object.freeze(
    Object.fromEntries(
      MCP_PROVIDERS.filter((p) => p.brand).map(
        (p) => [p.id, p.brand!] as const,
      ),
    ),
  );

export const HOST_SUFFIX_TO_PROVIDER: ReadonlyArray<readonly [string, string]> =
  MCP_PROVIDERS.flatMap((p) =>
    (p.hostSuffixes ?? []).map((suffix) => [suffix, p.id] as const),
  );

export const KNOWN_MCP_PROVIDERS: ReadonlyArray<string> = MCP_PROVIDERS.filter(
  (p) => p.brand,
).map((p) => p.id);
